'use client';

import { useTheme } from '@emotion/react';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { v4 as generateUUID } from 'uuid';
import { ImageProp } from '../../models/image';
import CheckSVG from '../../public/img/fill-check.svg';
import CloseSVG from '../../public/img/fill-close.svg';
import StarSVG from '../../public/img/star.svg';
import loadPhotoImg from '../../public/img/photo.png';
import { CommentPicture, Comment } from '../../models/comment';
import { ModalState } from '../../models';
import { ModalContentWrapper, ModalTitleWrapper } from '../common/Modal';
import { H2 } from '../styled/typography';
import Input from '../form/input';
import { Form } from '../styled/Form';
import { useAuthorization } from '../../providers/authorization';
import { Spinner } from '../loader/MainLoader';
import { PhoneInput } from '../form/phone-input';
import { getLocalStorageItem } from '../../lib/api/browserStorage';
import { EMAIL_REGEXP } from '../../utils/consts';

let heic2any: unknown;

if (typeof window !== 'undefined') {
  // eslint-disable-next-line global-require
  heic2any = require('heic2any');
}

export function CommentFormModal({
  reply,
  sendComment,
  setImages,
  setImagesSlider,
  setModalState,
  localStorageDescriptionKey,
}: {
  localStorageDescriptionKey: string;
  reply: Comment | null;
  sendComment: (params: {
    description: string;
    rating: number | null;
    loadedImages: CommentPicture[];
    name?: string;
    email?: string;
    phone_number?: string;
    reply: Comment | null;
    idempotency_key: string;
  }) => void;
  setImages: Dispatch<SetStateAction<ImageProp[]>>;
  setImagesSlider: Dispatch<
    SetStateAction<{ slide: number | null; visible: boolean }>
  >;
  setModalState: Dispatch<SetStateAction<ModalState>>;
}) {
  const inputTypes = {
    email: 'email',
    phone_number: 'phone_number',
  };
  const theme = useTheme();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const user = useAuthorization();
  const [inputType, setInputType] = useState(inputTypes.phone_number);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const prevInputTypeRef = useRef(inputType);

  const [loadedImages, setLoadedImages] = useState<CommentPicture[]>([]);
  const [loadedImagesForPreview, setLoadedImagesForPreview] = useState<
    CommentPicture[]
  >([]);
  const [rating, setRating] = useState<number | null>(0);
  const [isImgInConvertingProcess, setIsImgInConvertingProcess] =
    useState<boolean>(false);

  function phoneIsPossibleValidator(value?: string) {
    if (inputType === inputTypes.phone_number) {
      if (!value) {
        return true;
      }

      return isPossiblePhoneNumber(value);
    }
    return true;
  }

  function emailValidator(value?: string) {
    if (inputType === inputTypes.email) {
      if (!value) {
        return true;
      }

      return new RegExp(EMAIL_REGEXP).test(value);
    }
    return true;
  }

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string(),
        description: yup.string().required('Поле обязательно'),
        email:
          inputType !== inputTypes.phone_number
            ? yup.string().test('email', 'Некорректный email', emailValidator)
            : yup.string(),
        phone_number:
          inputType === inputTypes.phone_number
            ? yup
                .string()
                .test(
                  'phone_number',
                  'Некорректный номер телефона',
                  phoneIsPossibleValidator
                )
            : yup.string(),
      }),
    [inputType]
  );

  const {
    register,
    formState: { errors, isValid },
    setError: setFormError,
    watch,
    control,
    handleSubmit,
    trigger,
    clearErrors,
  } = useForm({
    defaultValues: {
      name: (user.isReady && user.user?.first_name) || '',
      email: (user.isReady && user.user?.email) || '',
      phone_number: (user.isReady && user.user?.phone_number) || '',
      description:
        JSON.parse(getLocalStorageItem(localStorageDescriptionKey) || '{}')
          .description || null,
    },
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onSubmit',
  });

  const descriptionValue: unknown = watch('description');
  const emailValue: unknown = watch('email');
  const phoneNumberValue: unknown = watch('phone_number');

  useEffect(() => {
    if (prevInputTypeRef.current !== inputType) {
      clearErrors(['email', 'phone_number', 'description']);
      if (inputType === inputTypes.phone_number) {
        trigger('phone_number');
      } else {
        trigger('email');
      }
    }
    prevInputTypeRef.current = inputType;
  }, [inputType, clearErrors, trigger]);

  const onSubmit = useCallback(
    (data: {
      name?: string;
      email?: string;
      phone_number?: string;
      description: string | null;
    }) => {
      const newData: {
        name?: string;
        email?: string;
        phone_number?: string;
        description: string;
        loadedImages: CommentPicture[];
        rating: number;
        reply: Comment | null;
        idempotency_key: string;
      } = {
        ...data,
        description: data.description || '',
        loadedImages: [],
        rating: 0,
        reply,
        idempotency_key: idempotencyKey,
      };

      if (!data.description) {
        return setFormError('description', {
          message: 'Это поле обязательно',
        });
      }

      // email
      if (inputType === inputTypes.email) {
        delete newData.phone_number;
        if (!new RegExp(EMAIL_REGEXP).test(data.email as string)) {
          return setFormError('email', {
            message: 'Введите пожалуйста корректный email',
          });
        }
      }

      // phone_number
      if (inputType === inputTypes.phone_number) {
        delete newData.email;
        if (!data.phone_number) {
          return setFormError('phone_number', {
            message: 'Введите пожалуйста корректный телефон',
          });
        }
        if (!isPossiblePhoneNumber(data.phone_number as string)) {
          return setFormError('phone_number', {
            message: 'Введите пожалуйста корректный телефон',
          });
        }
        newData.phone_number = data.phone_number.substring(1);
      }
      if (newData.name === '') {
        delete newData.name;
      }
      if (idempotencyKey) {
        newData.idempotency_key = idempotencyKey;
      }
      newData.loadedImages = loadedImages;
      newData.rating = rating || 0;
      return sendComment(newData);
    },
    [inputType, loadedImages, rating]
  );

  useEffect(() => {
    setLoadedImages([]);
    setLoadedImagesForPreview([]);
    setRating(0);
    setInputType(inputTypes.phone_number);
  }, []);

  useEffect(() => {
    const uuid = generateUUID();
    setIdempotencyKey(uuid);
  }, []);

  const openFile = () => fileRef?.current?.click();

  const handleChangeInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target?.files?.length || typeof window === 'undefined') return;
    const file = e.target.files[0];
    let convertedFile: null | File = null;
    if (file.name.toLowerCase().match(/\.hei[fc]$/)) {
      setIsImgInConvertingProcess(true);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const blob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 1,
      });
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      convertedFile = new File([blob], file.name, { type: 'image/jpeg' });
      setIsImgInConvertingProcess(false);
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        setLoadedImages((prev) => [
          ...prev,
          { id: prev.length, file, image: result },
        ]);
        setLoadedImagesForPreview((prev) => [
          ...prev,
          { id: prev.length, file: convertedFile || file, image: result },
        ]);
      }
    };
    reader.readAsDataURL(convertedFile || file);
  };

  const deleteImage = (ind: number) => {
    const copy = [...loadedImages];
    copy.splice(ind, 1);
    const copyPreview = [...loadedImagesForPreview];
    copy.splice(ind, 1);
    copyPreview.splice(ind, 1);
    setLoadedImages(copy);
    setLoadedImagesForPreview(copyPreview);
  };

  const handleImageClick = (id: number) => {
    setImages(
      loadedImagesForPreview.map((im, i) => ({
        id: i,
        image: im.image,
      }))
    );
    setImagesSlider({ slide: id, visible: true });
  };

  return (
    <ModalContentWrapper>
      <ModalTitleWrapper setModalState={setModalState}>
        <H2
          css={{
            fontSize: 26,
            fontWeight: 700,
            fontFamily: 'Montserrat',
            color: '#282828',
          }}
        >
          Написать отзыв
        </H2>
      </ModalTitleWrapper>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div css={{ padding: '0 30px 30px 30px' }}>
          <Input
            text='Имя'
            control={control}
            name='name'
            registerProps={register('name')}
          />
          {inputType === inputTypes.email && 'Использовать e-mail'}
          {inputType === inputTypes.phone_number && 'Использовать телефон'}
          <span
            onClick={() => {
              setInputType(
                inputType === inputTypes.email
                  ? inputTypes.phone_number
                  : inputTypes.email
              );
            }}
            css={{
              color: 'rgba(0, 0, 0, 0.25)',
              borderBottom: '1px dashed rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              position: 'relative',
              marginLeft: 15,
              '&:before': {
                content: '"|"',
                position: 'absolute',
                left: -10,
                top: -3,
                color: 'rgba(0, 0, 0, 0.3)',
                fontWeight: 400,
              },
            }}
          >
            {inputType === inputTypes.phone_number && 'Использовать e-mail'}
            {inputType === inputTypes.email && 'Использовать телефон'}
          </span>
          {inputType === inputTypes.email && (
            <Input
              text='E-mail'
              control={control}
              name='email'
              registerProps={register('email')}
              error={!!errors.email}
              errorMessage={errors.email?.message}
              secondaryText='Обязательное поле'
            />
          )}
          {inputType === inputTypes.phone_number && (
            <PhoneInput
              control={control}
              name='phone_number'
              error={!!errors.phone_number}
              errorMessage={errors.phone_number?.message}
              secondaryText='Обязательное поле'
            />
          )}
          <div css={{ marginTop: 16 }}>
            {/* {!!reply && <p css={{ marginBottom: 10 }}>В ответ {getName()}</p>} */}
            <div css={{ display: 'flex', gap: 12 }}>
              <p>Оценка</p>
              <span
                css={{
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  justifyContent: 'flex-start',
                  gap: 8,
                  '.comment__start-active': {
                    svg: {
                      fill: '#ffc907',
                    },
                  },
                }}
              >
                {[5, 4, 3, 2, 1].map((index) => (
                  <div
                    key={index}
                    className={
                      rating && index <= rating ? 'comment__start-active' : ''
                    }
                    css={{
                      '&:hover ~ &, &:hover': {
                        svg: {
                          opacity: 0.8,
                          fill: '#ffc907',
                        },
                      },
                    }}
                  >
                    <StarSVG
                      onClick={() => setRating(index === rating ? null : index)}
                      css={{
                        width: 16,
                        height: 16,
                        fill: '#bfc1c3',
                      }}
                    />
                  </div>
                ))}
              </span>
            </div>

            <div css={{ position: 'relative', marginTop: 8, marginBottom: 26 }}>
              <textarea
                placeholder='Написать комментарий'
                css={{
                  width: '100%',
                  minHeight: 128,
                  background: '#fff',
                  border: `2px solid ${
                    errors.description ? theme.colors.primary : '#dadada'
                  }`,
                  borderRadius: 4,
                  padding: '14px 20px',
                  margin: 0,
                  '&::placeholder': {
                    color: '#dadada',
                  },
                }}
                {...register('description')}
              />

              {typeof descriptionValue === 'string' &&
                !!descriptionValue.length && (
                  <CheckSVG
                    css={{
                      width: 20,
                      height: 20,
                      position: 'absolute',
                      right: '2.5%',
                      top: '40%',
                    }}
                  />
                )}
              {typeof descriptionValue === 'string' &&
                !descriptionValue.length && (
                  <CloseSVG
                    css={{
                      width: 20,
                      height: 20,
                      position: 'absolute',
                      right: '2.5%',
                      top: '40%',
                    }}
                  />
                )}
              {errors.description && (
                <div
                  css={{
                    textAlign: 'left',
                    position: 'absolute',
                    top: 132,
                    left: 0,
                    fontSize: '.75em',
                    color: '#f44336',
                  }}
                >
                  {errors.description.message}
                </div>
              )}
            </div>

            <p>Фотографии</p>

            <input
              type='file'
              ref={fileRef}
              accept='image/jpeg,image/png,image/heic'
              css={{ display: 'none' }}
              onChange={handleChangeInput}
            />

            <div
              css={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 10,
                marginTop: 16,
              }}
            >
              {loadedImagesForPreview.map((item, i) => (
                <div key={item.id}>
                  <div onClick={() => handleImageClick(i)}>
                    <img
                      src={item.image}
                      css={{ width: 90, height: 70, objectFit: 'cover' }}
                      alt=''
                    />
                  </div>

                  <button
                    css={{ marginTop: 10, color: theme.colors.primary }}
                    type='button'
                    onClick={() => deleteImage(i)}
                  >
                    Удалить
                  </button>
                </div>
              ))}

              {loadedImages.length < 10 && (
                <button
                  css={{
                    cursor: 'pointer',
                    position: 'relative',
                    width: 90,
                    height: 70,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: `2px dashed ${theme.colors.primary}`,
                    borderRadius: 4,
                  }}
                  type='button'
                  onClick={openFile}
                >
                  <img
                    alt='Загрузить фото'
                    src={loadPhotoImg.src}
                    css={{
                      width: 28,
                      opacity: isImgInConvertingProcess ? 0.2 : 1,
                    }}
                  />
                  {isImgInConvertingProcess && <Spinner />}
                </button>
              )}
            </div>

            <p
              css={{
                fontSize: 12,
                color: '#7D7B7A',
                marginTop: 15,
              }}
            >
              Можно прикрепить до 10 фотографий в формате PNG, JPG, HEIC
            </p>

            <div
              css={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <button
                css={{
                  background:
                    !isValid ||
                    (inputType === inputTypes.email &&
                      (!emailValue ||
                        typeof emailValue !== 'string' ||
                        emailValue.length === 0)) ||
                    (inputType === inputTypes.phone_number &&
                      (!phoneNumberValue ||
                        typeof phoneNumberValue !== 'string' ||
                        phoneNumberValue.length === 0))
                      ? theme.colors.grayStrong
                      : theme.colors.primary,
                  // backgroundImage:
                  //   'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
                  color: '#fff',
                  fontSize: 16,
                  padding: '9px 26px',
                  borderRadius: 40,
                  marginTop: 40,
                  transition: '.3s',
                  '&:hover': {
                    boxShadow: '0px 0px 10px 0px #de2839',
                  },
                }}
                type='submit'
              >
                Отправить отзыв
              </button>
            </div>
          </div>
        </div>
      </Form>
    </ModalContentWrapper>
  );
}
